import React, { useEffect, useRef, useState } from 'react';
import { IWebhookEventDef } from '../../../../networked-shared-interfaces/constants/webhook-catalog';
import { IWebhookDeliveryDetailResponse } from '../../../../networked-shared-interfaces/community-admin/external-vendor-webhooks';
import { WEBHOOK_TEST_MAX_POLLS, WebhooksApi } from './useWebhooks';
import { testResultLine } from './webhookHelpers';

interface IProps {
	api: Pick<WebhooksApi, 'testSend' | 'delivery'>;
	endpointId: string;
	events: readonly IWebhookEventDef[];
}

const WebhookTestSend = ({ api, endpointId, events }: IProps) => {
	const [event, setEvent] = useState(events[0]?.name ?? '');
	const [sending, setSending] = useState(false);
	const [deliveryId, setDeliveryId] = useState<string | null>(null);
	const [result, setResult] = useState<IWebhookDeliveryDetailResponse | null>(null);
	const [gaveUp, setGaveUp] = useState(false);
	const apiRef = useRef(api);
	apiRef.current = api;

	// Poll every 2 s until the test delivery settles, at most WEBHOOK_TEST_MAX_POLLS times.
	useEffect(() => {
		if (!deliveryId) return;
		let cancelled = false;
		let timer: any;
		let polls = 0;
		const tick = async () => {
			polls++;
			try {
				const d = await apiRef.current.delivery(deliveryId);
				if (cancelled) return;
				setResult(d);
				if (d.status !== 'pending') return;
			} catch {
				if (cancelled) return;
			}
			if (polls >= WEBHOOK_TEST_MAX_POLLS) return setGaveUp(true);
			timer = setTimeout(tick, 2000);
		};
		tick();
		return () => { cancelled = true; clearTimeout(timer); };
	}, [deliveryId]);

	const send = async () => {
		setSending(true);
		setResult(null);
		setGaveUp(false);
		setDeliveryId(null);
		const res = await api.testSend(endpointId, event);
		setSending(false);
		if (res) setDeliveryId(res.deliveryId);
	};

	const settled = result && result.status !== 'pending';
	return (
		<div className="form-group">
			<label><strong>Send a test event</strong></label>
			<div className="input-group">
				<select className="form-control" value={event} onChange={(e) => setEvent(e.target.value)} aria-label="Event">
					{events.map((e) => <option key={e.name} value={e.name} title={String(e.code)}>{e.label}</option>)}
				</select>
				<div className="input-group-append">
					<button type="button" className="btn btn-outline-primary" disabled={!event || sending} onClick={send}>{sending ? 'Sending...' : 'Send test'}</button>
				</div>
			</div>
			{deliveryId && (
				<small className="form-text text-muted">
					{settled ? testResultLine(result!) : gaveUp ? 'Still pending: check the delivery log.' : 'Sending...'}
				</small>
			)}
		</div>
	);
};

export default WebhookTestSend;
